import { Request } from 'express';
import { ReactElement } from 'react';
import { TrackPageViewContainer } from '../../../common/containers/TrackPageViewContainer';
import { getDehydratedState } from '../../../common/getDehydrateState';
import { t } from '../../../common/getTranslations';
import { Layout } from '../../../design-system/layout/Layout';
import PageLayout from '../../../domain/landing/presentational';
import { ApiErrorBoundary } from '../../../errors/ApiErrorBoundary.class';
import { addError } from '../../../errors/ErrorSlice';
import { thunkSerializeError } from '../../../errors/thunkSerializeError.function';
import { StaticLandingRankingLayoutQuery, StaticLandingRankingLayoutQueryVariables, StaticLandingRankingSection, StaticLandingType } from '../../../graphql/generated/types';
import { CommonMobileHeaderType } from '../../../header/constants';
import { PageViewCustomPropsDataKey } from '../../../logging/PageViewCustomPropsDataKey.enum';
import { setLayout } from '../../../pagewebLayout/PagewebLayoutSlice';
import { nextReduxWrapper } from '../../../state/store';
import { STATIC_LANDING_PAGE_NAME_MAP, STATIC_LANDING_PAGE_SECTION_NAME } from '../../../staticLanding/constants';
import { queryStaticLandingLayout } from '../../../staticLanding/queryStaticLandingLayout';
function StaticLandingRanking({
  layoutId,
  categoryName,
  viewType
}: {
  layoutId: string;
  categoryName: string;
  viewType: string;
}) {
  return <>
      <ApiErrorBoundary>
        <PageLayout id={layoutId}></PageLayout>
      </ApiErrorBoundary>
      <TrackPageViewContainer trackObj={{
      shouldSendUsagePage: true,
      section: STATIC_LANDING_PAGE_SECTION_NAME,
      page: `${STATIC_LANDING_PAGE_NAME_MAP[StaticLandingType.Ranking]}_${categoryName}`,
      actionType: 'Pageview',
      actionName: `${STATIC_LANDING_PAGE_NAME_MAP[StaticLandingType.Ranking]}_화면`,
      customProps: {
        user_action: 'pv',
        view_type: viewType
      }
    }} customPropsDataKeyList={[PageViewCustomPropsDataKey.PageCategoryFilter, PageViewCustomPropsDataKey.PageGenreFilter, PageViewCustomPropsDataKey.PageSort]} />
    </>;
}
StaticLandingRanking.getInitialProps = nextReduxWrapper.getInitialPageProps(store => async context => {
  const cookie = context?.req?.headers.cookie;
  const uidInfoList = context.query?.pathParams as string[];
  const query = context?.query;
  if (!uidInfoList || uidInfoList.length === 0) {
    return {
      notFound: true
    };
  }
  try {
    const {
      data,
      queryClient
    } = await queryStaticLandingLayout<StaticLandingRankingLayoutQuery, StaticLandingRankingLayoutQueryVariables>({
      ssrRequest: context.req as Request,
      type: StaticLandingType.Ranking,
      cookie,
      uidInfoList,
      query
    });
    if (!data || !data.staticLandingRankingLayout) {
      throw new Error('랭킹 고정랜딩 레이아웃 데이터가 없습니다.');
    }
    /**
     * TODO: getServerSideProps -> getInitialProps 로 넘어오면서 기존 hydrate 시 무시되던 page:0 이 getInitialProps 이후로 덮어쓰면서 백버튼을 통해 다시 들어올 시 무조건 0 페이지만 보여주는 현상이 있습니다.
     * react-query 로 변경하면 페이지 정보도 캐싱되기 때문에 자연스럽게 해결될 것으로 보이지만
     * 당장 변경하긴 어려우니 임시로 한번 가져왔던 layout 이면 redux 에 넣지 않도록 합니다.
     */
    const layoutId = data.staticLandingRankingLayout.id;
    if (!store.getState().pagewebLayout.entities.layouts[layoutId]) {
      store.dispatch(setLayout({
        layout: data.staticLandingRankingLayout
      }));
    }
    const staticLandingRankingSection = data.staticLandingRankingLayout.sections[0] as StaticLandingRankingSection;
    const categoryUid = staticLandingRankingSection.param.categoryUid;
    return {
      layoutId,
      categoryName: staticLandingRankingSection.categoryTabList.find(categoryInfo => categoryInfo.param === String(categoryUid))?.name,
      subcategoryName: staticLandingRankingSection.param.subcategory?.name,
      rankingTypeName: staticLandingRankingSection.param.rankingType?.name,
      viewType: staticLandingRankingSection.viewType,
      ...getDehydratedState(queryClient)
    };
  } catch (error) {
    store.dispatch(addError({
      error: thunkSerializeError(error)
    }));
    return {};
  }
});
StaticLandingRanking.getLayout = function getLayout(page: ReactElement) {
  const {
    categoryName,
    subcategoryName,
    rankingTypeName
  } = page.props;
  return <Layout title={t('landing_ranking')} mobileLayoutConfig={{
    shouldShowHeader: true,
    shouldShowFooter: true,
    headerType: CommonMobileHeaderType.TwoDepth,
    headerCustomTiaraObject: {
      customProps: {
        page_category_filter: categoryName,
        page_genre_filter: subcategoryName,
        page_sort: rankingTypeName
      }
    }
  }} pcLayoutConfig={{
    shouldShowHeader: true,
    shouldShowFooter: true
  }}>
      {page}
    </Layout>;
};
export default StaticLandingRanking;